import React, { useEffect, useState } from 'react';
import { getLogo } from '../services/api';
import { Link } from 'react-router-dom';

interface AnimationStyle {
  opacity: number;
  transform: string;
  transition: string;
}

const Hero: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Set visible after a short delay to ensure animation triggers
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const baseStyle: AnimationStyle = {
    opacity: 0,
    transform: 'translateY(20px)',
    transition: 'opacity 1000ms ease-out, transform 1000ms ease-out'
  };

  const visibleStyle = {
    opacity: 1,
    transform: 'translateY(0)'
  };

  return (
    <header className="min-h-[80vh] flex items-center justify-center relative overflow-hidden">
      {/* Background image layer */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center blur-sm"
        style={{ backgroundImage: 'url("/final_pictures/LI2025 (14).jpg")' }}
      />
      {/* Gradient overlay for readability */}
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-[#00529B]/70 to-[#003D74]/70" />
      <div className="container relative z-10">
        <div className="max-w-6xl mx-auto text-center">
          <div 
            className="mb-8"
            style={{
              ...baseStyle,
              ...(isVisible ? visibleStyle : {}),
              transitionDelay: '0ms'
            }}
          >
            <picture className="inline-block">
              <source media="(min-width: 768px)" srcSet={getLogo()} />
              <img 
                src={getLogo(true)}
                alt="Language Intelligence Conference 2025" 
                className="h-16 md:h-20 lg:h-24 mx-auto filter brightness-0 invert"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </picture>
          </div>
          <h1 
            className="text-title1 md:text-display font-bold mb-6 text-white"
            style={{
              ...baseStyle,
              ...(isVisible ? visibleStyle : {}),
              transitionDelay: '200ms'
            }}
          >
            Language Intelligence 2025
          </h1>
          <p 
            className="text-xl md:text-2xl mb-12 text-white/90 max-w-3xl mx-auto leading-relaxed"
            style={{
              ...baseStyle,
              ...(isVisible ? visibleStyle : {}),
              transitionDelay: '400ms'
            }}
          >
            Thank you for being part of the 2025 edition. Explore the photo gallery and presentations from this year's conference.
          </p>
          <div 
            className="flex flex-wrap items-center justify-center gap-4"
            style={{
              ...baseStyle,
              ...(isVisible ? visibleStyle : {}),
              transitionDelay: '600ms'
            }}
          >
            <Link
              to="/gallery"
              className="inline-block px-10 py-4 md:px-12 md:py-5 text-lg md:text-xl bg-white text-[#00529B] font-semibold rounded-full hover:bg-opacity-90 transition-colors shadow-xl hover:shadow-2xl"
            >
              View Photo Gallery
            </Link>
            <Link
              to="/presentations"
              className="inline-block px-10 py-4 md:px-12 md:py-5 text-lg md:text-xl bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-colors shadow-xl hover:shadow-2xl"
            >
              Presentations
            </Link>
            <Link
              to="/speakers"
              className="inline-block px-10 py-4 md:px-12 md:py-5 text-lg md:text-xl bg-white text-[#00529B] font-semibold rounded-full hover:bg-opacity-90 transition-colors shadow-xl hover:shadow-2xl"
            >
              Speakers
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Hero;
